import { Trans } from "@lingui/macro";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  EXPRESS_TRADING_NATIVE_TOKEN_WARN_HIDDEN_KEY,
  EXPRESS_TRADING_WRAP_OR_UNWRAP_WARN_HIDDEN_KEY,
} from "config/localStorage";
import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { useSubaccountContext } from "context/SubaccountContext/SubaccountContextProvider";
import { selectUpdateSubaccountSettings } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { ExpressTxnParams } from "domain/synthetics/express";
import { useChainId } from "lib/chains";
import { useGasPaymentTokensText } from "lib/gas/useGasPaymentTokensText";
import { useLocalStorageSerializeKey } from "lib/localStorage";
import { usePrevious } from "lib/usePrevious";
import { DEFAULT_SUBACCOUNT_EXPIRY_DURATION, DEFAULT_SUBACCOUNT_MAX_ALLOWED_COUNT } from "sdk/configs/express";
import { getNativeToken, getWrappedToken } from "sdk/configs/tokens";

import { ColorfulBanner, ColorfulButtonLink } from "components/ColorfulBanner/ColorfulBanner";

import ExpressIcon from "img/ic_express.svg?react";
import OneClickIcon from "img/ic_one_click.svg?react";

import { useExpressTradingWarnings } from "./hooks/useShowOneClickTradingInfo";

export function ExpressTradingWarningCard({
  expressParams,
  payTokenAddress,
  isWrapOrUnwrap,
  disabled,
  isGmxAccount,
  onAfterAction,
}: {
  expressParams: ExpressTxnParams | undefined;
  payTokenAddress: string | undefined;
  isWrapOrUnwrap: boolean;
  disabled?: boolean;
  isGmxAccount: boolean;
  onAfterAction?: () => void;
}) {
  const [isVisible, setIsVisible] = useState(true);
  const updateSubaccountSettings = useSelector(selectUpdateSubaccountSettings);
  const { setGasPaymentTokenAddress } = useSettings();
  const subaccountContext = useSubaccountContext();
  const history = useHistory();

  const { chainId } = useChainId();

  const [, setNativeTokenWarningHidden] = useLocalStorageSerializeKey(
    EXPRESS_TRADING_NATIVE_TOKEN_WARN_HIDDEN_KEY,
    false
  );

  const [, setWrapOrUnwrapWarningHidden] = useLocalStorageSerializeKey(
    EXPRESS_TRADING_WRAP_OR_UNWRAP_WARN_HIDDEN_KEY,
    false
  );

  const handleCloseNativeTokenWarningClick = useCallback(() => {
    setNativeTokenWarningHidden(true);
    setIsVisible(false);
  }, [setNativeTokenWarningHidden]);

  const handleCloseWrapOrUnwrapWarningClick = useCallback(() => {
    setWrapOrUnwrapWarningHidden(true);
    setIsVisible(false);
  }, [setWrapOrUnwrapWarningHidden]);

  const handleUpdateSubaccountSettings = useCallback(() => {
    updateSubaccountSettings({
      nextRemainigActions: BigInt(DEFAULT_SUBACCOUNT_MAX_ALLOWED_COUNT),
      nextRemainingSeconds: BigInt(DEFAULT_SUBACCOUNT_EXPIRY_DURATION),
      nextIsGmxAccount: isGmxAccount,
    }).then((success) => {
      if (success) {
        onAfterAction?.();
        setIsVisible(false);
      }
    });
  }, [updateSubaccountSettings, isGmxAccount, onAfterAction]);

  const handleDisableSubaccount = useCallback(async () => {
    const ok = await subaccountContext.tryDisableSubaccount();
    if (ok) onAfterAction?.();
  }, [subaccountContext, onAfterAction]);

  const handleUseWntAsGasToken = useCallback(() => {
    const wnt = getWrappedToken(chainId);
    setGasPaymentTokenAddress(wnt.address);
    window.dispatchEvent(new CustomEvent("gasPaymentTokenChanged"));
    onAfterAction?.();
  }, [chainId, setGasPaymentTokenAddress, onAfterAction]);

  const {
    shouldShowAllowedActionsWarning,
    shouldShowNativeTokenWarning,
    shouldShowWrapOrUnwrapWarning,
    shouldShowExpiredSubaccountWarning,
    shouldShowNonceExpiredWarning,
    shouldShowOutOfGasPaymentBalanceWarning,
    shouldShowSubaccountApprovalInvalidWarning,
    shouldShowExternalSwapSubaccountBlockedWarning,
    shouldShowExternalSwapGasConflictRequiredWarning,
    shouldShowExternalSwapGasConflictOptionalWarning,
  } = useExpressTradingWarnings({ expressParams, payTokenAddress, isWrapOrUnwrap, isGmxAccount });

  const prevShouldShowSubaccountApprovalInvalidWarning = usePrevious(shouldShowSubaccountApprovalInvalidWarning);
  const prevShouldShowExternalSwapSubaccountBlockedWarning = usePrevious(
    shouldShowExternalSwapSubaccountBlockedWarning
  );
  const prevShouldShowExternalSwapGasConflictRequiredWarning = usePrevious(
    shouldShowExternalSwapGasConflictRequiredWarning
  );
  const prevShouldShowExternalSwapGasConflictOptionalWarning = usePrevious(
    shouldShowExternalSwapGasConflictOptionalWarning
  );
  useEffect(() => {
    if (isVisible) return;
    const transitionedToTrue =
      (!prevShouldShowSubaccountApprovalInvalidWarning && shouldShowSubaccountApprovalInvalidWarning) ||
      (!prevShouldShowExternalSwapSubaccountBlockedWarning && shouldShowExternalSwapSubaccountBlockedWarning) ||
      (!prevShouldShowExternalSwapGasConflictRequiredWarning && shouldShowExternalSwapGasConflictRequiredWarning) ||
      (!prevShouldShowExternalSwapGasConflictOptionalWarning && shouldShowExternalSwapGasConflictOptionalWarning);
    if (transitionedToTrue) {
      setIsVisible(true);
    }
  }, [
    isVisible,
    prevShouldShowSubaccountApprovalInvalidWarning,
    shouldShowSubaccountApprovalInvalidWarning,
    prevShouldShowExternalSwapSubaccountBlockedWarning,
    shouldShowExternalSwapSubaccountBlockedWarning,
    prevShouldShowExternalSwapGasConflictRequiredWarning,
    shouldShowExternalSwapGasConflictRequiredWarning,
    prevShouldShowExternalSwapGasConflictOptionalWarning,
    shouldShowExternalSwapGasConflictOptionalWarning,
  ]);

  const { gasPaymentTokensText, gasPaymentTokenSymbols } = useGasPaymentTokensText(chainId);

  let content: ReactNode | undefined = undefined;
  let onCloseClick: undefined | (() => void) = undefined;
  let buttonText: ReactNode | undefined = undefined;
  let icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | undefined = undefined;
  let color: "blue" | "yellow" | "red" = "blue";

  let onClick: undefined | (() => void) = undefined;

  if (!isVisible || disabled) {
    return null;
  } else if (shouldShowWrapOrUnwrapWarning) {
    onCloseClick = handleCloseWrapOrUnwrapWarningClick;
    const nativeToken = getNativeToken(chainId);
    icon = ExpressIcon;
    content = (
      <Trans>Express Trading is unavailable for wrapping or unwrapping native token {nativeToken.symbol}</Trans>
    );
  } else if (shouldShowNativeTokenWarning) {
    const wrappedToken = getWrappedToken(chainId);
    const nativeToken = getNativeToken(chainId);
    onCloseClick = handleCloseNativeTokenWarningClick;
    icon = ExpressIcon;
    content = (
      <Trans>
        Express Trading is unavailable with the network's native token {nativeToken.symbol}. Consider using{" "}
        {wrappedToken.symbol} instead.
      </Trans>
    );
  } else if (shouldShowAllowedActionsWarning) {
    onClick = handleUpdateSubaccountSettings;
    icon = OneClickIcon;
    color = "yellow";
    content = <Trans>One-Click Trading action limit reached. You're now using Express Trading.</Trans>;
    buttonText = <Trans>Re-enable</Trans>;
  } else if (shouldShowNonceExpiredWarning) {
    onClick = handleUpdateSubaccountSettings;
    icon = OneClickIcon;
    color = "yellow";
    content = <Trans>One-Click Trading approval nonce expired. Re-sign to continue using One-Click Trading.</Trans>;
    buttonText = <Trans>Re-sign</Trans>;
  } else if (shouldShowExpiredSubaccountWarning) {
    onClick = handleUpdateSubaccountSettings;
    icon = OneClickIcon;
    color = "yellow";
    content = <Trans>One-Click Trading time limit expired. You're now using Express Trading.</Trans>;
    buttonText = <Trans>Re-enable</Trans>;
  } else if (shouldShowExternalSwapSubaccountBlockedWarning) {
    icon = OneClickIcon;
    color = "red";
    content = (
      <Trans>
        This swap requires external routing, which isn't available with One-Click Trading. Disable One-Click to use
        Express Trading.
      </Trans>
    );
    buttonText = <Trans>Disable One-Click Trading</Trans>;
    onClick = handleDisableSubaccount;
  } else if (shouldShowExternalSwapGasConflictRequiredWarning) {
    const wrappedToken = getWrappedToken(chainId);
    icon = ExpressIcon;
    color = "red";
    content = <Trans>This swap requires external routing. Pay gas in {wrappedToken.symbol} to enable it.</Trans>;
    buttonText = <Trans>Use {wrappedToken.symbol} for gas</Trans>;
    onClick = handleUseWntAsGasToken;
  } else if (shouldShowOutOfGasPaymentBalanceWarning) {
    icon = ExpressIcon;
    color = "yellow";
    content = <Trans>Express Trading and One-Click Trading are unavailable due to insufficient gas balance</Trans>;
    buttonText = <Trans>Buy {gasPaymentTokensText}</Trans>;
    onClick = () => {
      history.push(`/trade/swap?to=${gasPaymentTokenSymbols[0]}`);
      onAfterAction?.();
    };
  } else if (shouldShowSubaccountApprovalInvalidWarning) {
    icon = OneClickIcon;
    color = "yellow";
    content = (
      <Trans>
        One-Click Trading approval is invalid. This may happen when switching chains or changing payment tokens. Re-sign
        to continue using One-Click Trading.
      </Trans>
    );
    buttonText = <Trans>Re-sign</Trans>;
    onClick = handleUpdateSubaccountSettings;
  } else if (shouldShowExternalSwapGasConflictOptionalWarning) {
    const wrappedToken = getWrappedToken(chainId);
    icon = ExpressIcon;
    onCloseClick = () => setIsVisible(false);
    content = <Trans>Paying gas in {wrappedToken.symbol} may give a better rate on this swap</Trans>;
    buttonText = <Trans>Use {wrappedToken.symbol} for gas</Trans>;
    onClick = handleUseWntAsGasToken;
  } else {
    return null;
  }

  return (
    <ColorfulBanner color={color} icon={icon} onClose={onCloseClick}>
      {content}
      {onClick && (
        <ColorfulButtonLink color="blue" onClick={onClick}>
          {buttonText}
        </ColorfulButtonLink>
      )}
    </ColorfulBanner>
  );
}
